import React, { useEffect, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import useConsentStore from "../store/useConsentStore";
import { PRIVACY_POLICY_URL } from "../utils/privacyConsent";

const ConsentPreferencesDialog = () => {
  const posthog = usePostHog();
  const { preferencesOpen, analytics, consentGiven, doNotSell, gpcDetected, closePreferences, savePreferences } =
    useConsentStore();

  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);

  useEffect(() => {
    if (preferencesOpen) {
      setAnalyticsEnabled(
        consentGiven ? analytics : !gpcDetected
      );
    }
  }, [preferencesOpen, analytics, consentGiven, gpcDetected]);

  const handleSave = () => {
    savePreferences(posthog, { analytics: analyticsEnabled });
  };

  return (
    <Dialog
      open={preferencesOpen}
      onOpenChange={(open) => !open && closePreferences()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Privacy Preferences</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 text-sm text-muted-foreground">
          {gpcDetected && (
            <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
              Your browser&apos;s Global Privacy Control (GPC) signal was detected, so analytics is
              off by default. To enable it, turn on Analytics below and click Save Preferences.
            </p>
          )}

          <p>
            Manage how this site uses cookies and similar technologies. For more detail, see our{" "}
            <a
              href={PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-optonyprimary underline underline-offset-2">
              Privacy Policy
            </a>
            .
          </p>

          <div className="rounded-md border p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-foreground">Strictly Necessary</p>
                <p className="mt-1">
                  Required for authentication, security, and core app functionality.
                </p>
              </div>
              <span className="shrink-0 text-xs text-optonyprimary">Always active</span>
            </div>
          </div>

          <div className="rounded-md border p-4">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0 flex-1">
                <Label
                  htmlFor="analytics-consent"
                  className="font-medium text-foreground">
                  Analytics
                </Label>
                <p className="mt-1">Helps us understand how the tool is used.</p>
              </div>
              <Switch
                id="analytics-consent"
                checked={analyticsEnabled}
                onCheckedChange={setAnalyticsEnabled}
              />
            </div>
          </div>

          <p>{doNotSell ? " Your current preference is opted out." : ""}</p>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          <Button
            onClick={handleSave}
            className="w-full">
            Save Preferences
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConsentPreferencesDialog;
